import { Button } from '@base-ui/react/button';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { components } from '@/api/qraft/schema';
import { LANGUAGE_OPTIONS, type Language } from '~/constants/languages';
import buttonStyles from '~/styles/button.module.css';
import { fieldCss, selectCss } from './Form';

type ContentStatus = components['schemas']['ContentStatus'];
type QuestionType = components['schemas']['QuestionTypes'];

// Kept in the URL, so Back from a result returns to the same search.
export type ContentSearchParams = {
  q?: string;
  status?: ContentStatus;
  language?: Language;
  type?: QuestionType;
  page?: number;
};

const STATUSES: ContentStatus[] = ['draft', 'published'];
const LANGUAGES = LANGUAGE_OPTIONS.map(([code]) => code);

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]) =>
  allowed.includes(value as T) ? (value as T) : undefined;

export function parseContentSearch(
  s: Record<string, unknown>,
  types: readonly QuestionType[] = [],
): ContentSearchParams {
  const page = Number(s.page);
  // The router reads a bare `?q=29060` as a number, so an ID search in a link
  // arrives as one.
  const q = typeof s.q === 'number' ? String(s.q) : s.q;
  return {
    q: typeof q === 'string' && q !== '' ? q : undefined,
    status: oneOf(s.status, STATUSES),
    language: oneOf(s.language, LANGUAGES),
    type: oneOf(s.type, types),
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

// The query the search endpoints take, from what's in the URL.
export function searchQuery(params: ContentSearchParams, pageSize: number) {
  return {
    ...(params.q ? { search: params.q } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.language ? { language: params.language } : {}),
    limit: pageSize,
    offset: ((params.page ?? 1) - 1) * pageSize,
  };
}

export function SearchFilters({
  params,
  onChange,
  placeholder,
  typeLabels,
}: {
  params: ContentSearchParams;
  // A new filter starts from the first page.
  onChange: (patch: ContentSearchParams) => void;
  placeholder: string;
  // Questions only.
  typeLabels?: Record<QuestionType, string>;
}) {
  const [text, setText] = useState(params.q ?? '');
  // Follow the URL when something else changes it (the menu link, Back), or
  // the debounce below would write the old search back.
  const [urlQ, setUrlQ] = useState(params.q);
  if (params.q !== urlQ) {
    setUrlQ(params.q);
    if ((params.q ?? '') !== text.trim()) setText(params.q ?? '');
  }

  // Search once typing pauses, not on every key.
  useEffect(() => {
    if (text.trim() === (params.q ?? '')) return;
    const timer = setTimeout(
      () => onChange({ q: text.trim() || undefined, page: undefined }),
      300,
    );
    return () => clearTimeout(timer);
  }, [text, params.q, onChange]);

  const filtered =
    text.trim() !== '' ||
    params.q !== undefined ||
    params.status !== undefined ||
    params.language !== undefined ||
    params.type !== undefined;
  const clearFilters = () => {
    setText('');
    onChange({
      q: undefined,
      status: undefined,
      language: undefined,
      type: undefined,
      page: undefined,
    });
  };

  return (
    <Filters role="search">
      <SearchInput
        type="search"
        aria-label="Search by title or ID"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <FilterSelect
        aria-label="Status"
        value={params.status ?? ''}
        onChange={(e) =>
          onChange({
            status: (e.target.value || undefined) as ContentStatus | undefined,
            page: undefined,
          })
        }
      >
        <option value="">Any status</option>
        {STATUSES.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </FilterSelect>
      <FilterSelect
        aria-label="Language"
        value={params.language ?? ''}
        onChange={(e) =>
          onChange({
            language: (e.target.value || undefined) as Language | undefined,
            page: undefined,
          })
        }
      >
        <option value="">Any language</option>
        {LANGUAGE_OPTIONS.map(([code, label]) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </FilterSelect>
      {typeLabels && (
        <FilterSelect
          aria-label="Type"
          value={params.type ?? ''}
          onChange={(e) =>
            onChange({
              type: (e.target.value || undefined) as QuestionType | undefined,
              page: undefined,
            })
          }
        >
          <option value="">Any type</option>
          {Object.entries(typeLabels).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </FilterSelect>
      )}
      {filtered && (
        <Button className={buttonStyles.Button} onClick={clearFilters}>
          Clear filters
        </Button>
      )}
    </Filters>
  );
}

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const SearchInput = styled.input`
  ${fieldCss}
  flex: 1 1 18rem;
  font-size: 0.9375rem;
  width: auto;
`;

const FilterSelect = styled.select`
  ${fieldCss}
  ${selectCss}
  flex: 0 1 11rem;
  width: auto;
`;
